// Core
import React, { useState } from 'react';

// Hooks
import { Trans, useTranslation } from 'react-i18next';

// Libraries
import { useTheme } from 'styled-components';

// Component
import {
  DragArea,
  Element,
  FilePreview,
  FileUploaded,
  IconContainer,
  ImagePreview,
  Message,
  RemoveFile,
  TryAgainText,
  UploadContainer,
  UploadedContainer,
  UploadText,
} from './FileUpload.style';
import { FileUploadProps } from './FileUpload.types';

// Layout
import {
  CheckCircleFill,
  Image,
  TrashCan,
} from '../../icons';
import { Paragraph, ParagraphSize } from '../Paragraph';

// Types
import { Theme } from '../../../types/theme';

// Plugins
import { convertFileToBase64 } from '../../../plugins/general';

function FileUpload({
  accept,
  className,
  disabled = false,
  error = false,
  errorText,
  file,
  icon = <Image />,
  id,
  maxSize,
  onError,
  onRemove,
  onUpload,
  supportingText,
  testId,
}: FileUploadProps): JSX.Element {
  // Dependencies
  const { t } = useTranslation();
  const theme: Theme = useTheme();

  /* **********************************************************************************************
  **************************************** LOCAL STATES *******************************************
  ********************************************************************************************** */

  /* Initial state */
  const [dragging, setDragging] = useState<boolean>(false);
  const { source, fileName } = file || {};

  /* **********************************************************************************************
  ******************************************** METHODS ********************************************
  ********************************************************************************************** */

  /*
   * It verifies if the drag event is entering or leaving the element and sets the dragging state
   * @param event - Drag event [React.DragEvent<HTMLElement>]
   * @param isEntering - Flag to check if the event is entering or leaving the element [boolean]
   */
  const handleDragEvents = (event: React.DragEvent<HTMLElement>, isEntering: boolean): void => {
    event.preventDefault();
    const isFileDrag: boolean = event.dataTransfer.types.includes('Files');
    if (event.currentTarget.id === id && !source && !disabled) {
      setDragging(isEntering && isFileDrag);
    }
  };

  /*
   * It validates the file size and type and calls the onUpload callback
   * If the file is invalid, it throws an error message
   * @param uploadedFile - File to be uploaded
   * @returns boolean - True if the file is uploaded, false otherwise
   * @throws errorMessage - Error message to be displayed
   */
  const handleUpload = async (uploadedFile: File): Promise<boolean> => {
    let isValid: boolean = true;
    let errorMessage: string | null = null;

    // 1. Validation of Size
    if (maxSize && uploadedFile.size > maxSize) {
      const maxSizeMB: number = maxSize / (1024 * 1024);
      errorMessage = t('layout.elements.fileUpload.fileTooLarge', {
        maxSize: maxSizeMB,
        maxSizeMB,
      });
      isValid = false;
    }

    // 2. Validation of Type (always runs if accept is defined)
    if (accept) {
      const fileExtension: string | undefined = uploadedFile.name.split('.').pop()?.toLowerCase();
      const fileMimeType: string = uploadedFile.type || '';

      const acceptedTypes: string[] = accept
        .split(',')
        .map((type: string) => type.trim().toLowerCase());

      const fileExtWithDot: string | null = fileExtension ? `.${fileExtension}` : null;

      const isTypeValid: boolean = acceptedTypes.some((accType: string) => {
        // 1. Check by file extension (ex: ".jpg")
        if (fileExtWithDot && accType === fileExtWithDot) return true;

        // 2. Check by MIME type (ex: "image/png")
        if (fileMimeType && accType === fileMimeType) return true;

        // 3. Check by MIME type with wildcard (ex: "image/*")
        if (fileMimeType && accType.endsWith('/*')) {
          const baseMime: string = accType.split('/')[0];
          return fileMimeType.startsWith(`${baseMime}/`);
        }

        return false;
      });

      if (!isTypeValid) {
        errorMessage = t(
          'layout.elements.fileUpload.fileTypeNotAccepted',
          { accept },
        );
        isValid = false;
      }
    }

    // 3. Final processing
    // Always convert and call the onUpload callback, even if invalid
    const convertedFile: string = await convertFileToBase64(uploadedFile);
    if (onUpload) {
      onUpload({ fileName: uploadedFile.name, source: convertedFile });
    }

    // // If invalid, call the onError callback so parent can set error state
    if (!isValid && errorMessage) {
      setDragging(false);
      if (onError) {
        onError(errorMessage);
      }
    }

    // Always return 'false' to PREVENT the automatic upload of the AntD.
    // We already made the manual upload (converting to base64 and calling onUpload).
    return false;
  };

  /*
   * It handles the drop event and calls the handleUpload method
   * @param event - Drag event [React.DragEvent<HTMLElement
   */
  const onDrop = async (event: React.DragEvent<HTMLElement>): Promise<void> => {
    event.preventDefault();
    event.stopPropagation();

    if (event.dataTransfer && event.dataTransfer.files && event.dataTransfer.files.length > 0) {
      const uploadedFile: File = event.dataTransfer.files[0];
      await handleUpload(uploadedFile);
      setDragging(false);
    }
  };

  const getDragTextColor = (): string => {
    let textColor: string = theme.color.fileUpload.dragTextColor;
    if (dragging) {
      textColor = theme.color.fileUpload.draggingTextColor;
    } else if (disabled) {
      textColor = theme.color.fileUpload.disabledTextColor;
    }

    return textColor;
  };

  const getSupportingTextColor = (): string => {
    let textColor: string = theme.color.fileUpload.supportingTextColor;
    if (dragging) {
      textColor = theme.color.fileUpload.draggingTextColor;
    } else if (disabled && !file.source) {
      textColor = theme.color.fileUpload.disabledTextColor;
    } else if (file.source && error) {
      textColor = theme.color.fileUpload.errorTextColor;
    }

    return textColor;
  };

  return (
    <Element
      data-testid={testId}
      className={className}
      accept={accept}
      maxCount={1}
      disabled={disabled || !!source}
      onRemove={onRemove}
      beforeUpload={handleUpload}
      dragging={dragging}
      error={error}
      file={file}
    >
      {source ? (
        <UploadedContainer file={file} error={error}>
          <FilePreview>
            {!error && <CheckCircleFill />}
            {source.startsWith('data:image/') && !error ? (
              <ImagePreview src={source} alt={fileName} />
            ) : (
              <FileUploaded>{icon}</FileUploaded>
            )}
          </FilePreview>
          <Message>
            <Paragraph
              size={ParagraphSize.sm}
              color={
                error
                  ? theme.color.fileUpload.errorTextColor
                  : theme.color.fileUpload.uploaded.successTextColor
              }
            >
              {
                error
                  ? (errorText || t('layout.elements.fileUpload.error'))
                  : t('layout.elements.fileUpload.success')
              }
            </Paragraph>
            <Paragraph
              size={ParagraphSize.sm}
              color={getSupportingTextColor()}
              testId="file-name"
              preventOverflow
            >
              {fileName}
            </Paragraph>
            {error && (
              <TryAgainText role="button" onClick={onRemove}>
                {t('common.tryAgain')}
              </TryAgainText>
            )}
          </Message>
          <RemoveFile
            role="button"
            onClick={onRemove}
            error={error}
            data-testid="remove-file"
          >
            <TrashCan />
          </RemoveFile>
        </UploadedContainer>
      ) : (
        <UploadContainer dragging={dragging} file={file} disabled={disabled} error={error}>
          <DragArea
            onDragEnter={(event: React.DragEvent<HTMLElement>) => handleDragEvents(event, true)}
            onDragLeave={(event: React.DragEvent<HTMLElement>) => handleDragEvents(event, false)}
            onDrop={onDrop}
            id={id}
          />
          <IconContainer dragging={dragging} disabled={disabled}>
            {icon}
          </IconContainer>
          <UploadText>
            <Paragraph
              size={ParagraphSize.sm}
              color={getDragTextColor()}
              preventOverflow
            >
              <Trans>
                {t('layout.elements.fileUpload.clickOrDrag')}
              </Trans>
            </Paragraph>
            {(supportingText || accept) && (
              <Paragraph
                size={ParagraphSize.sm}
                color={getSupportingTextColor()}
                preventOverflow
              >
                {supportingText || accept}
              </Paragraph>
            )}
          </UploadText>
        </UploadContainer>
      )}
    </Element>
  );
}

export { FileUpload };
